import React, { useEffect, useMemo, useState } from 'react';
import { FaCode } from 'react-icons/fa';
import { Button } from 'rizzui';
import { toast } from 'sonner';
import {
  ContractTemplateProductTypeContext,
  ContractTemplateVariableCategory,
  filterContractTemplateVariableCategories,
} from '../../utils/contractTemplateVariables';

export interface ContractTemplateVariablesPanelProps {
  variables?: ContractTemplateVariableCategory[];
  defaultOpen?: boolean;
  className?: string;
  productType?: ContractTemplateProductTypeContext;
}

const ContractTemplateVariablesPanel: React.FC<ContractTemplateVariablesPanelProps> = ({
  variables,
  defaultOpen = false,
  className = '',
  productType,
}) => {
  const categories = useMemo(
    () => filterContractTemplateVariableCategories(variables, productType),
    [variables, productType]
  );

  const [open, setOpen] = useState<boolean>(defaultOpen);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  useEffect(() => {
    if (categories.length === 0) {
      setSelectedCategory('');
      return;
    }

    if (!selectedCategory || !categories.some((category) => category.type === selectedCategory)) {
      setSelectedCategory(categories[0].type);
    }
  }, [categories, selectedCategory]);

  const selectedVariables = useMemo(() => {
    return categories.find((cat) => cat.type === selectedCategory)?.variables ?? [];
  }, [categories, selectedCategory]);

  const copyVariable = async (variableKey: string) => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard API not supported');
      }
      await navigator.clipboard.writeText(variableKey);
      toast.success(`Variable ${variableKey} copiada al portapapeles.`);
    } catch (error) {
      console.error('Error copying variable:', error);
      toast.error('No se pudo copiar la variable. Intenta copiarla manualmente.');
    }
  };

  if (!categories.length) return null;

  return (
    <div className={className}>
      <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-lg border">
        <Button
          size="sm"
          variant={open ? 'solid' : 'outline'}
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2"
        >
          <FaCode className="w-4 h-4" />
          Variables
        </Button>
      </div>

      {open && (
        <div className="mb-4 p-4 bg-primary-50 rounded-lg border border-primary-200">
          <h3 className="font-semibold text-primary-900 mb-3">Variables Dinámicas</h3>

          <div className="flex flex-wrap gap-2 mb-3">
            {categories.map((category) => (
              <button
                key={category.type}
                type="button"
                onClick={() => setSelectedCategory(category.type)}
                className={`px-3 py-1 rounded text-sm ${
                  selectedCategory === category.type
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-primary-600 border border-primary-300 hover:bg-primary-50'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {selectedVariables.length > 0 ? (
              selectedVariables.map((variable, index) => (
                <button
                  key={`${variable.key}-${index}`}
                  type="button"
                  onClick={() => copyVariable(variable.key)}
                  className="p-2 text-left bg-white border border-gray-200 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  title={`Copiar: ${variable.key}`}
                >
                  <div className="text-sm font-medium text-gray-900">{variable.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{variable.key}</div>
                </button>
              ))
            ) : (
              <div className="col-span-full text-center text-gray-500 py-4">
                No hay variables disponibles para esta categoría
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractTemplateVariablesPanel;
